import React, { useCallback, useEffect, useRef, useState } from 'react';
import useStore from '../store/useStore';
import useBodyScrollLock from '../utils/useBodyScrollLock';

// ── GPS helpers ──────────────────────────────────────────────────────────────

const requestGPS = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      (err) => {
        if (err.code === 1) reject(new Error('Location access was denied. Please allow location permission or enter your pincode.'));
        else if (err.code === 2) reject(new Error('Unable to determine your position. Please enter your pincode manually.'));
        else reject(new Error('Location request timed out. Please enter your pincode manually.'));
      },
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 },
    );
  });

// ── Tab enum ─────────────────────────────────────────────────────────────────
const TAB_GPS     = 'gps';
const TAB_PINCODE = 'pincode';

// ── Component ────────────────────────────────────────────────────────────────

const LocationModal = () => {
  const {
    location_modal_open,
    location_loading,
    location_error,
    location_unserviceable_message,
    userLocation,
    enteredPincode,
    resolveByPincode,
    resolveByCoords,
    closeLocationModal,
  } = useStore();

  const [tab,          setTab]         = useState(TAB_GPS);
  const [pincode,      setPincode]     = useState('');
  const [pincodeErr,   setPincodeErr]  = useState('');
  const [gpsStatus,    setGpsStatus]   = useState('idle'); // idle | requesting | success | denied
  const [gpsError,     setGpsError]    = useState('');
  const [entered,      setEntered]     = useState(false); // animation gate

  const pincodeRef = useRef(null);

  // Scroll-lock while open
  useBodyScrollLock(location_modal_open);

  // Slide-in animation
  useEffect(() => {
    if (location_modal_open) {
      const t = setTimeout(() => setEntered(true), 20);
      return () => clearTimeout(t);
    } else {
      setEntered(false);
    }
  }, [location_modal_open]);

  // Reset local state when modal re-opens
  useEffect(() => {
    if (location_modal_open) {
      setTab(TAB_GPS);
      setPincode(enteredPincode ? String(enteredPincode) : '');
      setPincodeErr('');
      setGpsStatus('idle');
      setGpsError('');
    }
  }, [location_modal_open, enteredPincode]);

  // Auto-focus pincode input when that tab is active
  useEffect(() => {
    if (tab === TAB_PINCODE) {
      const t = setTimeout(() => pincodeRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [tab]);

  // ── GPS flow ────────────────────────────────────────────────────────────────

  const handleGPS = useCallback(async () => {
    setGpsStatus('requesting');
    setGpsError('');
    try {
      const { latitude, longitude } = await requestGPS();
      setGpsStatus('success');
      await resolveByCoords(latitude, longitude);
    } catch (err) {
      setGpsStatus('denied');
      setGpsError(err.message);
    }
  }, [resolveByCoords]);

  // ── Pincode flow ────────────────────────────────────────────────────────────

  const handlePincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPincode(val);
    if (pincodeErr) setPincodeErr('');
  };

  const handlePincodeSubmit = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(pincode)) {
      setPincodeErr('Please enter a valid 6-digit pincode.');
      pincodeRef.current?.focus();
      return;
    }
    setPincodeErr('');
    await resolveByPincode(pincode);
  };

  // Don't render anything when closed
  if (!location_modal_open) return null;

  const isLoading = location_loading || gpsStatus === 'requesting';

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center p-0 sm:p-4">

      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${entered ? 'opacity-100' : 'opacity-0'}`}
        // Dismiss only if user already has a location (not first-time modal)
        onClick={userLocation ? closeLocationModal : undefined}
      />

      {/* Modal card */}
      <div
        className={`relative w-full sm:max-w-md transform transition-all duration-300 ease-out
          ${entered ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'}
          rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl overflow-hidden`}
      >
        {/* ── Green header strip ── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#046a38] via-[#057840] to-emerald-500 px-6 pt-6 pb-8">
          {/* Decorative blobs */}
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -left-6 bottom-0 h-24 w-24 rounded-full bg-white/10" />

          <div className="relative flex items-start justify-between">
            <div>
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 shadow-inner backdrop-blur-sm">
                {/* Map pin SVG */}
                <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0Z" />
                  <circle cx="12" cy="11" r="3" />
                </svg>
              </div>
              <h2 className="text-xl font-extrabold text-white">Set Delivery Location</h2>
              <p className="mt-1 max-w-xs text-sm text-emerald-100">
                Help us show products available in your area and accurate delivery options.
              </p>
            </div>

            {/* Close — only when user already has a saved location */}
            {userLocation && (
              <button
                onClick={closeLocationModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30"
                aria-label="Close"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-gray-100 bg-white">
          {[
            { key: TAB_GPS,     label: 'Use My Location', icon: (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
              </svg>
            )},
            { key: TAB_PINCODE, label: 'Enter Pincode', icon: (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 10h8M8 14h5"/>
              </svg>
            )},
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex flex-1 items-center justify-center gap-2 border-b-2 py-3 text-sm font-semibold transition-colors ${
                tab === key
                  ? 'border-[#046a38] text-[#046a38]'
                  : 'border-transparent text-gray-400 hover:text-gray-700'
              }`}
            >
              {icon}
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="px-6 py-6">

          {/* GPS Tab */}
          {tab === TAB_GPS && (
            <div className="text-center">
              {gpsStatus === 'idle' && (
                <>
                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
                    <svg className="h-10 w-10 text-[#046a38]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
                    </svg>
                  </div>
                  <p className="mb-1 text-base font-bold text-gray-900">Detect Automatically</p>
                  <p className="mb-5 text-sm text-gray-500">
                    We'll use your device's GPS to find your nearest delivery area. Your location is never stored on our servers.
                  </p>
                  <button
                    id="location-gps-btn"
                    onClick={handleGPS}
                    disabled={isLoading}
                    className="w-full rounded-2xl bg-gradient-to-r from-[#046a38] to-emerald-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-900/20 transition-all hover:-translate-y-0.5 hover:shadow-xl active:scale-95 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    📍 Use My Current Location
                  </button>
                </>
              )}

              {gpsStatus === 'requesting' && (
                <div className="flex flex-col items-center gap-4 py-4">
                  <div className="relative">
                    <div className="h-16 w-16 animate-spin rounded-full border-4 border-emerald-100 border-t-[#046a38]" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="h-6 w-6 text-[#046a38]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0Z" />
                        <circle cx="12" cy="11" r="3" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Detecting your location…</p>
                    <p className="mt-1 text-sm text-gray-400">Please allow location access if prompted.</p>
                  </div>
                </div>
              )}

              {gpsStatus === 'success' && location_loading && (
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-100 border-t-[#046a38]" />
                  <p className="text-sm font-semibold text-gray-700">Resolving your area…</p>
                </div>
              )}

              {gpsStatus === 'denied' && (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-left">
                  <p className="mb-1 text-sm font-bold text-red-700">Location access denied</p>
                  <p className="text-xs text-red-600">{gpsError}</p>
                  <button
                    onClick={() => setTab(TAB_PINCODE)}
                    className="mt-3 text-xs font-semibold text-[#046a38] underline"
                  >
                    Enter pincode instead →
                  </button>
                </div>
              )}

              {gpsStatus === 'success' && !location_loading && (location_unserviceable_message || location_error) && (
                <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {location_unserviceable_message || location_error}
                </div>
              )}
            </div>
          )}

          {/* Pincode Tab */}
          {tab === TAB_PINCODE && (
            <form onSubmit={handlePincodeSubmit} noValidate>
              <label htmlFor="location-pincode" className="mb-1.5 block text-sm font-semibold text-gray-700">
                Enter your 6-digit Pincode
              </label>

              <div className={`flex items-center gap-3 rounded-2xl border-2 bg-gray-50 px-4 py-3 transition-all ${
                pincodeErr ? 'border-red-400 bg-red-50/30' : 'border-gray-200 focus-within:border-[#046a38] focus-within:bg-white'
              }`}>
                <svg className="h-5 w-5 shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0Z" />
                  <circle cx="12" cy="11" r="3" />
                </svg>
                <input
                  id="location-pincode"
                  ref={pincodeRef}
                  type="tel"
                  inputMode="numeric"
                  placeholder="e.g. 412201"
                  value={pincode}
                  onChange={handlePincodeChange}
                  maxLength={6}
                  className="flex-1 bg-transparent text-base font-semibold text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400"
                />
                {pincode.length === 6 && (
                  <svg className="h-5 w-5 shrink-0 text-[#046a38]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {pincodeErr && (
                <p className="mt-1.5 text-xs font-medium text-red-500">{pincodeErr}</p>
              )}

              {(location_unserviceable_message || location_error) && tab === TAB_PINCODE && (
                <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
                  {location_unserviceable_message || location_error}
                </p>
              )}

              <button
                id="location-pincode-btn"
                type="submit"
                disabled={isLoading || pincode.length !== 6}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#046a38] to-emerald-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-900/20 transition-all hover:-translate-y-0.5 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {isLoading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Checking…
                  </>
                ) : (
                  'Confirm Location'
                )}
              </button>
            </form>
          )}
        </div>

        {/* ── Footer note ── */}
        <div className="border-t border-gray-100 bg-gray-50 px-6 py-3 text-center">
          <p className="text-xs text-gray-400">
            🔒 Your location is only used to show local delivery options.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LocationModal;
